import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useT } from '@/context/ContentContext';

gsap.registerPlugin(ScrollTrigger);

export const Philosophy: React.FC = () => {
  const kicker = useT('home.philosophy.kicker', 'רהאם מנסור');
  const line1 = useT('home.philosophy.quote.line1', '"אני לא רק מטפלת בעור.');
  const line2a = useT('home.philosophy.quote.line2a', 'אני מחזירה את ');
  const line2hl = useT('home.philosophy.quote.line2highlight', 'ההרמוניה');
  const line3 = useT('home.philosophy.quote.line3', 'בין הגוף לנפש."');
  const cta = useT('home.philosophy.cta', 'קראי עוד על רהאם');
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    // Split text into word spans, preserving <br> and inline color spans
    const wrapWords = (node: ChildNode) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (!text.trim()) return;
        const frag = document.createDocumentFragment();
        const parts = text.split(/(\s+)/);
        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else if (part.length) {
            const span = document.createElement('span');
            span.className = 'word-reveal inline-block';
            span.style.opacity = '0';
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.parentNode?.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'BR') return;
        // Recurse children (copy first since we mutate)
        Array.from(el.childNodes).forEach(wrapWords);
      }
    };

    Array.from(heading.childNodes).forEach(wrapWords);

    const words = Array.from(heading.querySelectorAll<HTMLElement>('.word-reveal'));
    if (!words.length) return;

    const trigger = ScrollTrigger.create({
      trigger: heading,
      start: 'top 85%',
      end: 'bottom 50%',
      scrub: true,
      onUpdate: (self) => {
        const progress = self.progress;
        const total = words.length;
        words.forEach((word, index) => {
          const wp = index / total;
          const np = (index + 1) / total;
          let opacity = 0;
          if (progress >= np) {
            opacity = 1;
          } else if (progress >= wp) {
            const fp = (progress - wp) / (np - wp);
            opacity = fp;
          }
          word.style.opacity = String(opacity);
        });
      },
    });

    ScrollTrigger.refresh();

    return () => {
      trigger.kill();
    };
  }, []);

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="relative w-full z-[30] -mt-[35vh] flex flex-col items-center"
    >
      <div className="absolute top-[35vh] left-0 right-0 bottom-0 bg-[#f2ede2] -z-10"></div>

      <div
        ref={contentRef}
        className="max-w-6xl w-full relative z-10 px-6 flex flex-col items-center min-h-screen justify-center pb-32"
      >
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          <span className="text-sm md:text-base font-bold tracking-[0.2em] text-[#67392b] uppercase block mb-8">
            {kicker}
          </span>

          <div className="mb-16 w-full max-w-3xl mx-auto">
            <h2
              ref={headingRef}
              className="text-5xl md:text-7xl lg:text-8xl font-serif text-[#1F1A17] leading-[1.1] tracking-wide text-center"
            >
              {line1}
              <br />
              {line2a}<span className="text-[#67392b]">{line2hl}</span>
              <br />
              {line3}
            </h2>
          </div>

          <Link
            to="/about"
            className="inline-flex items-center gap-4 text-sm uppercase tracking-[0.15em] font-medium py-4 px-8 border border-[#67392b]/30 text-[#67392b] hover:bg-[#67392b] hover:text-[#F9F7F2] transition-all duration-500 group"
          >
            <span>{cta}</span>
            <ArrowLeft size={16} className="group-hover:-translate-x-2 transition-transform duration-500" />
          </Link>
        </div>
      </div>
    </section>
  );
};
